import { cn } from "utils/helpers";
import { formatCurrency } from "./utils";
import type { QuoteTableColumn, QuoteTableMode } from "./quote-table.types";
import type { RfxQuoteItem } from "../../Submission/types/quote.type";
import type { UnitOption } from "./DynamicQuoteTable";

export interface QuoteTableCellProps {
	allValues?: Record<string, Record<string, number | string>>;
	column: QuoteTableColumn;
	columns: QuoteTableColumn[];
	columnValues?: Record<number, Record<number, string>>;
	disabled: boolean;
	dirtyRowValues?: Record<number, { name?: string; unit_id?: number | null }>;
	editValues?: Record<number, Record<number, { quantity: string; rate: string }>>;
	isPrimaryDescription: boolean;
	item: RfxQuoteItem;
	mode: QuoteTableMode;
	onCellValueChange?: (itemId: number, columnId: number, field: string, value: string) => void;
	onFormikFieldChange?: (itemId: number, field: string, value: number | string) => void;
	onRowUpdate?: (rowId: number, payload: { name?: string; unit_id?: number | null }) => void;
	rowErrors?: Record<number, { name?: boolean; unit_id?: boolean; cells?: Record<number, boolean> }>;
	unitOptions?: UnitOption[];
}

const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
	if (event.key === "-") {
		event.preventDefault();
	}
};

const QuoteTableCell: React.FC<QuoteTableCellProps> = ({
	allValues = {},
	column: col,
	columns,
	columnValues = {},
	disabled,
	dirtyRowValues = {},
	editValues = {},
	isPrimaryDescription,
	item,
	mode,
	onCellValueChange,
	onFormikFieldChange,
	onRowUpdate,
	rowErrors = {},
	unitOptions = [],
}) => {
	const isProcurerView = mode === "edit";
	const isClientView = mode === "view";

	if (col.columnType === "description") {
		if (isPrimaryDescription) {
			if (isProcurerView && !disabled && onRowUpdate) {
				return (
					<textarea
						className={cn(
							"w-full resize-none overflow-hidden rounded border bg-white px-2 py-1 text-sm",
							rowErrors[item.id]?.name ? "border-red-500" : "border-gray-300"
						)}
						rows={1}
						value={dirtyRowValues[item.id]?.name ?? item.name ?? ""}
						onChange={(e) => {
							e.target.style.height = "auto";
							e.target.style.height = `${e.target.scrollHeight}px`;
							onRowUpdate(item.id, { name: e.target.value });
						}}
						onFocus={(e) => {
							e.target.style.height = "auto";
							e.target.style.height = `${e.target.scrollHeight}px`;
						}}
						data-testid={`row-name-input-${item.id}`}
					/>
				);
			}
			return <span className="text-gray-900 whitespace-pre-wrap break-words">{item.name || "-"}</span>;
		}
		// Additional description columns — always procurer-editable
		const value = columnValues[item.id]?.[col.id] ?? "";
		if (disabled) {
			return <span className="text-gray-900 whitespace-pre-wrap break-words">{value || "-"}</span>;
		}
		const hasCellError = rowErrors[item.id]?.cells?.[col.id] === true;
		return (
			<textarea
				className={cn(
					"w-full resize-none overflow-hidden rounded border bg-white px-2 py-1 text-sm",
					hasCellError ? "border-red-500" : "border-gray-300"
				)}
				rows={1}
				value={value}
				onChange={(e) => {
					e.target.style.height = "auto";
					e.target.style.height = `${e.target.scrollHeight}px`;
					onCellValueChange?.(item.id, col.id, "value", e.target.value);
				}}
				onFocus={(e) => {
					e.target.style.height = "auto";
					e.target.style.height = `${e.target.scrollHeight}px`;
				}}
				placeholder="Enter description"
				disabled={disabled}
				data-testid={`desc-col-${col.id}-item-${item.id}`}
			/>
		);
	}

	if (col.columnType === "response_text") {
		const formikField = `text_${col.id}`;
		const key = `quote-${item.id}`;
		const formikValue = allValues[key]?.[formikField];
		const value = formikValue !== undefined && formikValue !== null
			? String(formikValue)
			: (columnValues[item.id]?.[col.id] ?? "");
		if (isProcurerView || (isClientView && !value)) {
			return <span className="text-sm italic text-gray-400">Supplier response</span>;
		}
		if (isClientView) {
			return <span className="text-gray-900 whitespace-pre-wrap break-words">{value || "-"}</span>;
		}
		return (
			<textarea
				className="w-full resize-none overflow-hidden rounded border border-gray-300 bg-white px-2 py-1 text-sm"
				rows={1}
				value={value}
				onChange={(e) => {
					e.target.style.height = "auto";
					e.target.style.height = `${e.target.scrollHeight}px`;
					onFormikFieldChange?.(item.id, formikField, e.target.value);
				}}
				onFocus={(e) => {
					e.target.style.height = "auto";
					e.target.style.height = `${e.target.scrollHeight}px`;
				}}
				placeholder="Enter response"
				disabled={disabled}
				data-testid={`resp-col-${col.id}-item-${item.id}`}
			/>
		);
	}

	if (col.columnType === "response_number" || col.columnType === "response_quantity" || col.columnType === "total") {
		const key = `quote-${item.id}`;
		const formikValues = allValues[key];
		const hasCellError = rowErrors[item.id]?.cells?.[col.id] === true;

		// Auto-calculate total: if quantity column exists and has a value > 0, multiply qty × each response_value and sum; otherwise just sum response_values
		if (col.columnType === "total" && col.isSupplierEditable === false) {
			const valueColIds = columns
				.filter((c) => c.columnType === "response_value")
				.map((c) => c.id);
			const quantityCol = columns.find((c) => c.columnType === "response_quantity");
			const rawQty = quantityCol
				? Number(formikValues?.["quantity"] ?? columnValues[item.id]?.[quantityCol.id] ?? 0)
				: 0;
			const useMultiplier = quantityCol && rawQty > 0;
			const total = valueColIds.reduce((sum, colId) => {
				const formikVal = formikValues?.[`rate_${colId}`];
				const val = Number(formikVal ?? columnValues[item.id]?.[colId] ?? editValues[item.id]?.[colId]?.rate ?? 0);
				return sum + (useMultiplier ? rawQty * val : val);
			}, 0);
			return (
				<span className="text-right px-2 block text-gray-900">
					{formatCurrency(isNaN(total) ? 0 : total)}
				</span>
			);
		}

		// Default columns: read/write Formik values (quantity for number, rate for currency)
		const formikField = col.columnType === "response_quantity" ? "quantity" : "rate";
		const formikValue = formikValues?.[formikField];
		const hasFormikValue = formikValue !== undefined && formikValue !== null;
		const value = hasFormikValue ? String(formikValue) : (columnValues[item.id]?.[col.id] ?? "");

		if (isProcurerView) {
			if (col.columnType === "total") {
				return <span className="px-2 text-right block text-sm text-gray-400">&mdash;</span>;
			}
			return <span className="text-sm text-gray-400">0</span>;
		}

		if (isClientView || disabled) {
			const display = col.columnType === "total" && value
				? formatCurrency(Number(value))
				: value || "-";
			return <span className={cn("text-gray-900", col.columnType === "total" && "text-right block")}>{display}</span>;
		}

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const numericValue = parseFloat(e.target.value);
			const safeValue = isNaN(numericValue) || numericValue < 0 ? "" : numericValue;
			if (hasFormikValue || onFormikFieldChange) {
				onFormikFieldChange?.(item.id, formikField, safeValue);
			} else {
				onCellValueChange?.(item.id, col.id, "value", e.target.value);
			}
		};

		return (
			<div className={col.columnType === "total" ? "relative flex" : undefined}>
				{col.columnType === "total" && (
					<span className="absolute left-2 top-1/2 z-10 -translate-y-1/2 text-sm">$</span>
				)}
				<input
					type="number"
					className={cn(
						"w-full rounded border bg-white px-2 py-1 text-sm",
						hasCellError ? "border-red-500" : "border-gray-300",
						col.columnType === "total" && "pl-5"
					)}
					value={value === "" ? "" : value}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					placeholder="0"
					min={0}
					step={col.columnType === "total" ? 1 : undefined}
					disabled={disabled}
					data-testid={`num-col-${col.id}-item-${item.id}`}
				/>
			</div>
		);
	}

	if (col.columnType === "unit_of_measurement") {
		if (isProcurerView && !disabled && onRowUpdate && unitOptions.length > 0) {
			return (
				<select
					className={cn(
						"w-full rounded border bg-white px-1 py-1 text-sm",
						rowErrors[item.id]?.unit_id ? "border-red-500" : "border-gray-300"
					)}
					value={dirtyRowValues[item.id]?.unit_id ?? item.unitId ?? ""}
					onChange={(e) => {
						const val = e.target.value;
						onRowUpdate(item.id, { unit_id: val ? Number(val) : null });
					}}
					data-testid={`row-unit-select-${item.id}`}
				>
					<option value="">-</option>
					{unitOptions.map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
			);
		}
		return <span className="text-gray-900">{item.unitMeasurement ?? "-"}</span>;
	}

	if (col.columnType === "response_value") {
		const formikField = `rate_${col.id}`;
		const key = `quote-${item.id}`;
		const formikValue = allValues[key]?.[formikField];
		const value = formikValue !== undefined && formikValue !== null
			? String(formikValue)
			: (columnValues[item.id]?.[col.id] ?? editValues[item.id]?.[col.id]?.rate ?? "");
		const hasCellError = rowErrors[item.id]?.cells?.[col.id] === true;

		if (isProcurerView) {
			return <span className="text-sm text-gray-400">{formatCurrency(0)}</span>;
		}
		if (disabled || isClientView) {
			return <span className="text-gray-900">{value ? formatCurrency(Number(value)) : "-"}</span>;
		}

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const numericValue = parseFloat(e.target.value);
			const safeValue = isNaN(numericValue) || numericValue < 0 ? "" : numericValue;
			onFormikFieldChange?.(item.id, formikField, safeValue);
		};

		return (
			<div className="relative flex">
				<span className="absolute left-2 top-1/2 z-10 -translate-y-1/2 text-sm text-gray-500">$</span>
				<input
					type="number"
					className={cn(
						"w-full rounded border pl-5 pr-2 py-1 text-sm",
						hasCellError ? "border-red-500" : "border-gray-300"
					)}
					value={value === "" ? "" : value}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					min={0}
					disabled={disabled}
					placeholder="0"
					data-testid={`val-col-${col.id}-item-${item.id}`}
				/>
			</div>
		);
	}

	return null;
};

export { QuoteTableCell };
